import React, { useEffect, useRef, useState } from 'react';
import "./ExamResults.css";
import api from '../RefreshToken/RefreshToken';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { MAIN_URL } from '../../constants';
import { ArrowLeft, Check, ClipboardX, RefreshCw, X } from 'lucide-react';
import { Badge, Button, Card, EmptyState, PageHeader, ProgressBar, Skeleton } from '../ui';
import { AttemptSwitcher } from './ExamWidgets';
import { chapterLabel, isCanceledRequest, normalizeAttempts, pickChapterId, sortChapters } from './examData';
import './ExamResultPage.css';

// Faqat ko'rsatish uchun: butun son o'zgarishsiz, kasr — 1 xonagacha.
const formatScore = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const number = Number(value);
    if (Number.isNaN(number)) return String(value);
    return Number.isInteger(number) ? String(number) : number.toFixed(1);
};

const hasValue = (value) => value !== null && value !== undefined && value !== '';

function ResultSkeleton() {
    return (
        <Card>
            <div className="ui-exam__skeleton" aria-busy="true">
                <Skeleton width="40%" height={20} />
                <Skeleton width={96} height={40} />
                <Skeleton height={12} />
                <Skeleton width="80%" height={12} />
                <Skeleton width="60%" height={12} />
            </div>
        </Card>
    );
}

function LoadError({ onRetry }) {
    return (
        <Card padding="none">
            <EmptyState
                icon={<ClipboardX />}
                title="Natijani yuklab bo'lmadi"
                description="Internet aloqasini tekshirib, qayta urinib ko'ring"
                action={<Button size="sm" variant="secondary" onClick={onRetry}>Qayta urinish</Button>}
            />
        </Card>
    );
}

// A bo'lim: savollar ro'yxati
function SectionA({ test }) {
    const items = Array.isArray(test?.items) ? test.items : [];
    return (
        <Card title="A bo'lim — test" subtitle={`Ball: ${formatScore(test?.overal)}`}>
            {test?.feedback && <p className="ui-exam__feedback">{test.feedback.replace(/(\d+\.\s?)/g, '\n$1').trim()}</p>}
            {items.length === 0 ? (
                <p className="ui-exam__muted">Savollar mavjud emas</p>
            ) : (
                <ol className="ui-exam__questions">
                    {items.map((item, index) => {
                        const selectedAnswer = item.selected_option || item.answer;
                        return (
                            <li key={index} className={`ui-exam__question ${item.is_correct ? 'ui-exam__question--correct' : 'ui-exam__question--wrong'}`}>
                                <span className="ui-exam__question-icon" aria-label={item.is_correct ? "To'g'ri" : "Noto'g'ri"}>
                                    {item.is_correct ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}
                                </span>
                                <div className="ui-exam__question-body">
                                    <p className="ui-exam__question-text">
                                        <span className="ui-exam__question-number">{index + 1}.</span> {item.question}
                                    </p>
                                    {Array.isArray(item.options) && item.options.length > 0 && (
                                        <ul className="ui-exam__options">
                                            {item.options.map((option, i) => (
                                                <li key={i} className="ui-exam__option">{option}</li>
                                            ))}
                                        </ul>
                                    )}
                                    <dl className="ui-exam__answers">
                                        {hasValue(selectedAnswer) && (
                                            <div>
                                                <dt>Tanlangan javob</dt>
                                                <dd>{selectedAnswer}</dd>
                                            </div>
                                        )}
                                        {hasValue(item.correct_option) && (
                                            <div>
                                                <dt>To'g'ri javob</dt>
                                                <dd>{item.correct_option}</dd>
                                            </div>
                                        )}
                                    </dl>
                                    {hasValue(item.explanation) && <p className="ui-exam__explanation">{item.explanation}</p>}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            )}
        </Card>
    );
}

// B bo'lim: masalalar (ball / maksimal ball bo'lsa ProgressBar)
function SectionB({ sectionB }) {
    const tasks = Array.isArray(sectionB?.tasks) ? sectionB.tasks : [];
    return (
        <Card title="B bo'lim — masalalar" subtitle={`Ball: ${formatScore(sectionB?.overal)}`}>
            {sectionB?.feedback && <p className="ui-exam__feedback">{sectionB.feedback}</p>}
            {tasks.length === 0 ? (
                <p className="ui-exam__muted">Masalalar mavjud emas</p>
            ) : (
                <ol className="ui-exam__tasks">
                    {tasks.map((task, i) => (
                        <li key={i} className="ui-exam__task">
                            <div className="ui-exam__task-head">
                                <span className="ui-exam__task-title">{task.title || `${i + 1}-masala`}</span>
                                <span className="ui-exam__task-score">
                                    {formatScore(task.score)}{hasValue(task.max_score) ? ` / ${formatScore(task.max_score)}` : ''}
                                </span>
                            </div>
                            {hasValue(task.max_score) && Number(task.max_score) > 0 && (
                                <ProgressBar
                                    value={(Number(task.score) / Number(task.max_score)) * 100}
                                    tone="success"
                                    label={`${i + 1}-masala balli`}
                                />
                            )}
                            {hasValue(task.problem) && (
                                <div className="ui-exam__task-block">
                                    <span className="ui-exam__label">Masala</span>
                                    <p className="ui-exam__pre">{task.problem}</p>
                                </div>
                            )}
                            {hasValue(task.answer) && (
                                <div className="ui-exam__task-block">
                                    <span className="ui-exam__label">Javob</span>
                                    <p className="ui-exam__pre">{task.answer}</p>
                                </div>
                            )}
                            {hasValue(task.answer_file_url) && (
                                <a href={task.answer_file_url} target="_blank" rel="noopener noreferrer" className="ui-exam__link">
                                    Javob fayli
                                </a>
                            )}
                        </li>
                    ))}
                </ol>
            )}
        </Card>
    );
}

const ExamResult = () => {
    const TOKEN = localStorage.getItem('admin_token');
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Id'lar avval URL query'dan, bo'lmasa state'dan olinadi. Ikkalasi ham bo'lmasa so'rov yuborilmaydi.
    const studentId = searchParams.get('studentId') || location.state?.id || null;
    const courseId = searchParams.get('courseId') || location.state?.courseId || null;
    const requestedChapterId = searchParams.get('chapterId');
    const hasIds = Boolean(studentId && courseId);

    // Boblar ro'yxati yuklanganda qaysi bob tanlanishi (effekt dependency'si emas).
    const requestedChapterRef = useRef(requestedChapterId);
    requestedChapterRef.current = requestedChapterId;

    const [chapters, setChapters] = useState({ status: 'idle', items: [] });
    const [chapterId, setChapterId] = useState(null);
    const [results, setResults] = useState({ status: 'idle', items: [] });
    const [attemptIndex, setAttemptIndex] = useState(null);
    const [chaptersReload, setChaptersReload] = useState(0);
    const [resultsReload, setResultsReload] = useState(0);

    // Imtihon topshirilgan boblar
    useEffect(() => {
        if (!hasIds) return undefined;
        const controller = new AbortController();
        setChapters((prev) => ({ status: 'loading', items: prev.items }));
        api.get(`${MAIN_URL}/management/students/${studentId}/${courseId}/examed/chapters`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${TOKEN}`
            },
            signal: controller.signal,
        }).then((res) => {
            if (controller.signal.aborted) return;
            const items = sortChapters(res?.data);
            setChapters({ status: 'success', items });
            setChapterId((current) => pickChapterId(items, requestedChapterRef.current ?? current));
        }).catch((err) => {
            if (isCanceledRequest(err) || controller.signal.aborted) return;
            console.log(err);
            setChapters({ status: 'error', items: [] });
        });
        return () => controller.abort();
    }, [hasIds, studentId, courseId, TOKEN, chaptersReload]);

    // Tanlangan bob natijalari. Bob almashganda eski natija darhol tozalanadi,
    // oldingi so'rov bekor qilinadi (kech kelgan javob yangisining ustiga yozilmaydi).
    useEffect(() => {
        if (!hasIds || chapterId === null) {
            setResults({ status: 'idle', items: [] });
            return undefined;
        }
        const controller = new AbortController();
        setResults({ status: 'loading', items: [] });
        setAttemptIndex(null);
        api.get(`/management/students/${studentId}/${courseId}/${chapterId}/exam-results`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${TOKEN}`
            },
            signal: controller.signal,
        }).then((res) => {
            if (controller.signal.aborted) return;
            setResults({ status: 'success', items: Array.isArray(res?.data) ? res.data : [] });
        }).catch((err) => {
            if (isCanceledRequest(err) || controller.signal.aborted) return;
            console.log(err);
            setResults({ status: 'error', items: [] });
        });
        return () => controller.abort();
    }, [hasIds, studentId, courseId, chapterId, TOKEN, resultsReload]);

    // Tanlangan bob URL'ga ham yoziladi (sahifa yangilansa saqlanadi); route path o'zgarmaydi.
    const handleChapterClick = (id) => {
        setChapterId(id);
        const query = new URLSearchParams({ studentId, courseId, chapterId: id });
        navigate(`${location.pathname}?${query.toString()}`, { replace: true, state: location.state });
    };

    const handleRefresh = () => {
        setChaptersReload((x) => x + 1);
        setResultsReload((x) => x + 1);
    };

    const attempts = normalizeAttempts(results.items);
    const selectedIndex = attemptIndex ?? attempts.length - 1;
    const selected = attempts[selectedIndex]?.result;

    const selectedChapter = chapters.items.find((chapter) => chapter.id === chapterId);
    const courseTitle = location.state?.courseTitle || selected?.chapter?.course?.title;
    const subtitle = [location.state?.studentName, courseTitle].filter(Boolean).join(' — ') || undefined;

    const header = (
        <>
            <div>
                <Button variant="ghost" size="sm" leftIcon={<ArrowLeft />} onClick={() => navigate(-1)}>
                    Orqaga
                </Button>
            </div>
            <PageHeader
                title="Imtihon natijalari"
                subtitle={subtitle}
                actions={
                    hasIds && (
                        <Button variant="secondary" leftIcon={<RefreshCw />} onClick={handleRefresh}>
                            Yangilash
                        </Button>
                    )
                }
            />
        </>
    );

    if (!hasIds) {
        return (
            <div className="ui-exam">
                {header}
                <Card padding="none">
                    <EmptyState
                        title="Imtihon natijasi topilmadi"
                        description="Studentlar ro'yxatiga qaytib, qaytadan oching"
                        action={<Button size="sm" onClick={() => navigate('/students')}>Studentlar ro'yxati</Button>}
                    />
                </Card>
            </div>
        );
    }

    let content;
    if (chapters.status === 'idle' || (chapters.status === 'loading' && chapters.items.length === 0)) {
        content = (
            <div className="ui-exam__layout">
                <div className="ui-exam__chapters" aria-busy="true">
                    <Skeleton height={40} />
                    <Skeleton height={40} />
                    <Skeleton height={40} />
                </div>
                <ResultSkeleton />
            </div>
        );
    } else if (chapters.status === 'error') {
        content = <LoadError onRetry={() => setChaptersReload((x) => x + 1)} />;
    } else if (chapters.items.length === 0) {
        content = (
            <Card padding="none">
                <EmptyState title="Student hali imtihon topshirmagan" description="Imtihon topshirilgach natijalar shu yerda ko'rinadi" />
            </Card>
        );
    } else {
        content = (
            <div className="ui-exam__layout">
                <nav className="ui-exam__chapters" aria-label="Boblar">
                    {chapters.items.map((chapter) => {
                        const active = chapter.id === chapterId;
                        return (
                            <button
                                key={chapter.id}
                                type="button"
                                className={`ui-exam__chapter ${active ? 'ui-exam__chapter--active' : ''}`}
                                aria-current={active ? 'true' : undefined}
                                title={chapter.title}
                                onClick={() => handleChapterClick(chapter.id)}
                            >
                                <span className="ui-exam__chapter-order">{chapterLabel(chapter)}</span>
                                <span className="ui-exam__chapter-title">{chapter.title}</span>
                                {active && results.status === 'success' && attempts.length > 0 && (
                                    <span className="ui-exam__chapter-attempts">{attempts.length} urinish</span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                <div className="ui-exam__main">
                    {results.status === 'loading' || results.status === 'idle' ? (
                        <ResultSkeleton />
                    ) : results.status === 'error' ? (
                        <LoadError onRetry={() => setResultsReload((x) => x + 1)} />
                    ) : !selected ? (
                        <Card padding="none">
                            <EmptyState title="Bu bob bo'yicha natija topilmadi" />
                        </Card>
                    ) : (
                        <>
                            <Card>
                                <div className="ui-exam__summary">
                                    <div className="ui-exam__summary-head">
                                        <div>
                                            <span className="ui-exam__label">
                                                {selectedChapter ? chapterLabel(selectedChapter) : 'Bob'}
                                            </span>
                                            <h2 className="ui-exam__chapter-heading">{selected.chapter?.title || selectedChapter?.title}</h2>
                                        </div>
                                        <AttemptSwitcher attempts={attempts} value={selectedIndex} onChange={setAttemptIndex} />
                                    </div>
                                    <div className="ui-exam__score-row">
                                        <span className="ui-exam__score">{formatScore(selected.total_score)}</span>
                                        <span className="ui-exam__score-label">umumiy ball</span>
                                        <Badge variant={selected.passed ? 'success' : 'danger'} dot>
                                            {selected.passed ? "O'tdi" : "O'tmadi"}
                                        </Badge>
                                    </div>
                                    <span className="ui-exam__muted">Urinishlar: {attempts.length}</span>
                                </div>
                            </Card>
                            <SectionA test={selected.test} />
                            <SectionB sectionB={selected.section_b} />
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="ui-exam">
            {header}
            {content}
        </div>
    );
};

export default ExamResult;
